import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/DashboardLayout';
import ScrollToTopButton from './components/ScrollToTopButton';
import WhatsAppButton from './components/WhatsAppButton';
import CookieConsent, { applyConsent, getConsent } from './components/CookieConsent';
import './App.css';

// Eager — küçük / auth-critical pages
import AuthPage from './pages/AuthPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Lazy — tüm diğer sayfalar (code splitting)
const LandingPage             = lazy(() => import('./pages/LandingPage'));
const HakkimizdaPage          = lazy(() => import('./pages/HakkimizdaPage'));
const IletisimPage            = lazy(() => import('./pages/IletisimPage'));
const NelerYaparPage          = lazy(() => import('./pages/NelerYaparPage'));
const AdminLandingContentPage = lazy(() => import('./pages/AdminLandingContentPage'));
const Dashboard               = lazy(() => import('./pages/Dashboard'));
const ReportsPage             = lazy(() => import('./pages/ReportsPage'));
const MyReportsPage           = lazy(() => import('./pages/MyReportsPage'));
const ListingsPage            = lazy(() => import('./pages/ListingsPage'));
const CreateRealEstateReport  = lazy(() => import('./pages/CreateRealEstateReport'));
const ReportTypeSelectorPage  = lazy(() => import('./pages/ReportTypeSelectorPage'));
const CreateValuationReport   = lazy(() => import('./pages/CreateValuationReport'));
const EditReportPage          = lazy(() => import('./pages/EditReportPage'));
const ReportDetailPageNew     = lazy(() => import('./pages/ReportDetailPageNew'));
const SeoSettingsPage         = lazy(() => import('./pages/SeoSettingsPage'));
const SiteSettingsPage        = lazy(() => import('./pages/SiteSettingsPage'));
const NotificationsPage       = lazy(() => import('./pages/NotificationsPage'));
const ProfilePage             = lazy(() => import('./pages/ProfilePage'));
const AdminUsersPage          = lazy(() => import('./pages/AdminUsersPage'));
const AdminStatsPage          = lazy(() => import('./pages/AdminStatsPage'));
const AdminStoresPage         = lazy(() => import('./pages/AdminStoresPage'));
const ConsultantStatsPage     = lazy(() => import('./pages/ConsultantStatsPage'));
const StoreConsultantsPage    = lazy(() => import('./pages/StoreConsultantsPage'));
const StoreStatsPage          = lazy(() => import('./pages/StoreStatsPage'));
const StoreReportsPage        = lazy(() => import('./pages/StoreReportsPage'));
const ApiKeysPage             = lazy(() => import('./pages/ApiKeysPage'));
const PublicReportPage        = lazy(() => import('./pages/PublicReportPage'));
const ReportPreviewPage       = lazy(() => import('./pages/ReportPreviewPage'));
const SupportPage             = lazy(() => import('./pages/SupportPage'));
const AdminTicketsPage        = lazy(() => import('./pages/AdminTicketsPage'));
const GoalsPage               = lazy(() => import('./pages/GoalsPage'));
const GoalsPerformancePage    = lazy(() => import('./pages/GoalsPerformancePage'));
const StoreGoalsPage          = lazy(() => import('./pages/StoreGoalsPage'));
const MonthlyDeclarationPage  = lazy(() => import('./pages/MonthlyDeclarationPage'));
const StoreDeclarationsPage   = lazy(() => import('./pages/StoreDeclarationsPage'));
const AdminContactPage        = lazy(() => import('./pages/AdminContactPage'));
const AdminPackagesPage       = lazy(() => import('./pages/AdminPackagesPage'));
const BlogPage                = lazy(() => import('./pages/BlogPage'));
const BlogPostPage            = lazy(() => import('./pages/BlogPostPage'));
const AdminBlogPage           = lazy(() => import('./pages/AdminBlogPage'));
const AdminBlogEditPage       = lazy(() => import('./pages/AdminBlogEditPage'));
const AdminBlogCategoriesPage = lazy(() => import('./pages/AdminBlogCategoriesPage'));
const EmsalSozlukPage         = lazy(() => import('./pages/EmsalSozlukPage'));
const NotFoundPage            = lazy(() => import('./pages/NotFoundPage'));
const LegalPage               = lazy(() => import('./pages/LegalPage'));
const AdminLegalPage          = lazy(() => import('./pages/AdminLegalPage'));
const ImageEditorPage         = lazy(() => import('./pages/editor/ImageEditorPage'));
const AdminDiskPage           = lazy(() => import('./pages/AdminDiskPage'));
const SaleRemindersPage       = lazy(() => import('./pages/SaleRemindersPage'));
const RentalContractsPage     = lazy(() => import('./pages/RentalContractsPage'));
const HowToUsePage            = lazy(() => import('./pages/HowToUsePage'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const GA_ID = 'G-Z1E4T6WWH5';

const GoogleAnalyticsTracker = () => {
  const location = useLocation();
  useEffect(() => {
    const consent = getConsent();
    if (typeof window.gtag === 'function' && consent?.analytics) {
      window.gtag('config', GA_ID, { page_path: location.pathname + location.search });
    }
  }, [location]);
  return null;
};

const SiteMetaApplier = () => {
  useEffect(() => {
    axios.get('/api/site-settings').then(({ data }) => {
      // Browser title
      if (data.site_title) document.title = data.site_title;

      // Meta description
      if (data.site_description) {
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
          meta = document.createElement('meta');
          meta.name = 'description';
          document.head.appendChild(meta);
        }
        meta.content = data.site_description;
      }

      // Favicon
      if (data.favicon_url) {
        ['icon', 'shortcut icon'].forEach(rel => {
          let link = document.querySelector(`link[rel="${rel}"]`);
          if (!link) { link = document.createElement('link'); link.rel = rel; document.head.appendChild(link); }
          link.href = data.favicon_url;
        });
      }

      // OG site name
      if (data.site_title) {
        let ogSiteName = document.querySelector('meta[property="og:site_name"]');
        if (!ogSiteName) {
          ogSiteName = document.createElement('meta');
          ogSiteName.setAttribute('property', 'og:site_name');
          document.head.appendChild(ogSiteName);
        }
        ogSiteName.setAttribute('content', data.site_title);
      }

      // Twitter card default
      if (!document.querySelector('meta[name="twitter:card"]')) {
        const tc = document.createElement('meta');
        tc.name = 'twitter:card';
        tc.content = 'summary_large_image';
        document.head.appendChild(tc);
      }

      // Google Tag Manager
      if (data.google_tag_manager_id) {
        const gtmId = data.google_tag_manager_id;
        if (!document.getElementById('gtm-script')) {
          const s = document.createElement('script');
          s.id = 'gtm-script';
          s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`;
          document.head.appendChild(s);
        }
        // GTM noscript body injection
        if (!document.getElementById('gtm-noscript')) {
          const ns = document.createElement('noscript');
          ns.id = 'gtm-noscript';
          const iframe = document.createElement('iframe');
          iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
          iframe.height = '0';
          iframe.width = '0';
          iframe.style.cssText = 'display:none;visibility:hidden';
          ns.appendChild(iframe);
          document.body.insertBefore(ns, document.body.firstChild);
        }
      }

      // Google Analytics (GA4 or UA)
      if (data.google_analytics_id && !data.google_tag_manager_id) {
        const gaId = data.google_analytics_id;
        if (!document.getElementById('ga-script')) {
          const s = document.createElement('script');
          s.id = 'ga-script';
          s.async = true;
          s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
          document.head.appendChild(s);
          const i = document.createElement('script');
          i.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
          document.head.appendChild(i);
        }
      }

      // Facebook Pixel
      if (data.facebook_pixel_id) {
        const pixelId = data.facebook_pixel_id;
        if (!document.getElementById('fbpixel-script')) {
          const s = document.createElement('script');
          s.id = 'fbpixel-script';
          s.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixelId}');fbq('track','PageView');`;
          document.head.appendChild(s);
        }
      }
    }).catch(() => {});
  }, []);
  return null;
};

const RedirectIfLoggedIn = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  return user ? <Navigate to="/dashboard" replace /> : children;
};

function App() {
  return (
    <LanguageProvider>
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <GoogleAnalyticsTracker />
          <SiteMetaApplier />
          <ScrollToTopButton />
          <WhatsAppButton />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/neler-yapar" element={<NelerYaparPage />} />
            <Route path="/hakkimizda" element={<HakkimizdaPage />} />
            <Route path="/iletisim" element={<IletisimPage />} />
            <Route path="/login" element={<RedirectIfLoggedIn><AuthPage /></RedirectIfLoggedIn>} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/public/report/:publicToken" element={<PublicReportPage />} />
            {/* Görsel editör: DashboardLayout OLMADAN tam ekran */}
            <Route path="/image-editor" element={<ProtectedRoute><React.Suspense fallback={<PageLoader/>}><ImageEditorPage /></React.Suspense></ProtectedRoute>} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/gizlilik-politikasi" element={<LegalPage />} />
            <Route path="/kullanim-kosullari" element={<LegalPage />} />
            <Route path="/kvkk" element={<LegalPage />} />

            <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/reports" element={<ProtectedRoute roles={['admin', 'super_admin', 'store_admin']}><ReportsPage /></ProtectedRoute>} />
              <Route path="/my-reports" element={<MyReportsPage />} />
              <Route path="/reports/new" element={<ReportTypeSelectorPage />} />
              <Route path="/reports/new/sales" element={<CreateRealEstateReport />} />
              <Route path="/reports/new/valuation" element={<CreateValuationReport />} />
              <Route path="/reports/:reportId" element={<ReportDetailPageNew />} />
              <Route path="/reports/:reportId/preview" element={<ReportPreviewPage />} />
              <Route path="/reports/:reportId/edit" element={<EditReportPage />} />
              <Route path="/reports/:reportId/edit-valuation" element={<CreateValuationReport />} />
<Route path="/reports/:reportId/seo" element={<SeoSettingsPage />} />
              <Route path="/listings" element={<ProtectedRoute roles={['admin', 'super_admin', 'store_admin', 'consultant']}><ListingsPage /></ProtectedRoute>} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/settings" element={<ProfilePage />} />
              
              <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsersPage /></ProtectedRoute>} />
              <Route path="/admin/stats" element={<ProtectedRoute adminOnly><AdminStatsPage /></ProtectedRoute>} />
              <Route path="/admin/stores" element={<ProtectedRoute roles={['admin', 'super_admin']}><AdminStoresPage /></ProtectedRoute>} />
              <Route path="/admin/api-keys" element={<ProtectedRoute adminOnly><ApiKeysPage /></ProtectedRoute>} />
              <Route path="/admin/site-settings" element={<ProtectedRoute adminOnly><SiteSettingsPage /></ProtectedRoute>} />
              <Route path="/admin/landing-content" element={<ProtectedRoute adminOnly><AdminLandingContentPage /></ProtectedRoute>} />

              <Route path="/store/consultants" element={<ProtectedRoute roles={['store_admin']}><StoreConsultantsPage /></ProtectedRoute>} />
              <Route path="/store/stats" element={<ProtectedRoute roles={['store_admin']}><StoreStatsPage /></ProtectedRoute>} />
              <Route path="/store/reports" element={<ProtectedRoute roles={['store_admin']}><StoreReportsPage /></ProtectedRoute>} />

              <Route path="/my-stats" element={<ConsultantStatsPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/goals" element={<GoalsPage />} />
              <Route path="/goals/performance" element={<GoalsPerformancePage />} />
              <Route path="/store/goals" element={<ProtectedRoute roles={['store_admin']}><StoreGoalsPage /></ProtectedRoute>} />
              <Route path="/monthly-declaration" element={<MonthlyDeclarationPage />} />
              <Route path="/store/declarations" element={<ProtectedRoute roles={['store_admin']}><StoreDeclarationsPage /></ProtectedRoute>} />
              <Route path="/admin/tickets" element={<ProtectedRoute adminOnly><AdminTicketsPage /></ProtectedRoute>} />
              <Route path="/admin/contact" element={<ProtectedRoute adminOnly><AdminContactPage /></ProtectedRoute>} />
              <Route path="/admin/packages" element={<ProtectedRoute adminOnly><AdminPackagesPage /></ProtectedRoute>} />
              <Route path="/admin/blog" element={<ProtectedRoute adminOnly><AdminBlogPage /></ProtectedRoute>} />
              <Route path="/admin/blog/new" element={<ProtectedRoute adminOnly><AdminBlogEditPage /></ProtectedRoute>} />
              <Route path="/admin/blog/:postId/edit" element={<ProtectedRoute adminOnly><AdminBlogEditPage /></ProtectedRoute>} />
              <Route path="/admin/blog/categories" element={<ProtectedRoute adminOnly><AdminBlogCategoriesPage /></ProtectedRoute>} />
              <Route path="/admin/legal" element={<ProtectedRoute adminOnly><AdminLegalPage /></ProtectedRoute>} />
              <Route path="/admin/disk" element={<ProtectedRoute adminOnly><AdminDiskPage /></ProtectedRoute>} />
              <Route path="/sale-reminders" element={<SaleRemindersPage />} />
              <Route path="/rental-contracts" element={<RentalContractsPage />} />
              <Route path="/how-to-use" element={<HowToUsePage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <CookieConsent />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
